import { useState, useEffect, useRef, type ReactNode } from 'react';
import { cariDB } from '../../lib/cariDB';
import { PATIENT_FIELDS } from './constants';
import type { PatientFormData } from './types';
import { dbSurgeries } from '../../lib/localDB';

type Props = {
  patientData: PatientFormData;
  onPatientChange: (key: keyof PatientFormData, value: string) => void;
  accentColor: 'teal' | 'amber';
  headerExtra?: ReactNode;
};

const ACCENT_MAP = {
  teal: {
    iconBg: 'bg-teal-50', iconText: 'text-teal-600',
    inputRing: 'focus:ring-teal-400',
    triggerActive: 'border-teal-300 bg-teal-50 text-teal-800',
    triggerHover: 'hover:border-teal-300',
    hospitalIcon: 'text-teal-500',
    clearHover: 'hover:bg-teal-200 text-teal-500',
    listItemActive: 'bg-teal-50 text-teal-700',
    listItemHover: 'hover:bg-teal-50',
    listAvatarBg: 'bg-teal-100 text-teal-600',
    listCheck: 'text-teal-500',
    addBg: 'bg-teal-500',
    addHover: 'hover:bg-teal-50',
    addText: 'text-teal-600',
    btnActive: 'bg-teal-500 text-white',
    btnHover: 'hover:bg-teal-50 hover:text-teal-600',
  },
  amber: {
    iconBg: 'bg-amber-50', iconText: 'text-amber-600',
    inputRing: 'focus:ring-amber-400',
    triggerActive: 'border-amber-300 bg-amber-50 text-amber-800',
    triggerHover: 'hover:border-amber-300',
    hospitalIcon: 'text-amber-500',
    clearHover: 'hover:bg-amber-200 text-amber-500',
    listItemActive: 'bg-amber-50 text-amber-700',
    listItemHover: 'hover:bg-amber-50',
    listAvatarBg: 'bg-amber-100 text-amber-600',
    listCheck: 'text-amber-500',
    addBg: 'bg-amber-500',
    addHover: 'hover:bg-amber-50',
    addText: 'text-amber-600',
    btnActive: 'bg-amber-500 text-white',
    btnHover: 'hover:bg-amber-50 hover:text-amber-600',
  },
};

export default function PatientFormCard({ patientData, onPatientChange, accentColor, headerExtra }: Props) {
  const a = ACCENT_MAP[accentColor];

  const [clinicOptions, setClinicOptions] = useState<string[]>([]);
  const [patientOptions, setPatientOptions] = useState<string[]>([]);
  const [clinicDropdownOpen, setClinicDropdownOpen] = useState(false);
  const [clinicSearch, setClinicSearch] = useState('');
  const clinicRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    cariDB.getAllClinicsForSettings().then((list) => {
      setClinicOptions(list.map((c) => c.clinic_name));
    });
    dbSurgeries.getAll().then((surgeries) => {
      const names = Array.from(new Set(surgeries.map(s => s.patient_name).filter(n => n?.trim() !== '')));
      setPatientOptions(names);
    });
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (clinicRef.current && !clinicRef.current.contains(e.target as Node)) {
        setClinicDropdownOpen(false);
        setClinicSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 flex items-center justify-center ${a.iconBg} rounded-lg`}>
            <i className={`ri-user-line ${a.iconText} text-sm`}></i>
          </div>
          <h2 className="text-sm font-bold text-gray-800">Hasta Bilgileri</h2>
        </div>
        {headerExtra}
      </div>
      <div className="px-6 py-5 space-y-4">
        {PATIENT_FIELDS.map((field) => (
          <div key={field.key} className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-600 w-20 flex-shrink-0">
              {field.label}
            </label>
            {field.type === 'date' && (
              <input
                type="date"
                value={patientData.operation_date}
                onChange={(e) => onPatientChange('operation_date', e.target.value)}
                className={`px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 ${a.inputRing} focus:border-transparent transition-all`}
              />
            )}
            {field.type === 'text' && (
              <>
                <input
                  type="text"
                  list={field.key === 'patient_name' ? 'patient-names-list' : undefined}
                  autoComplete="off"
                  value={patientData[field.key] as string}
                  onChange={(e) => onPatientChange(field.key, e.target.value)}
                  placeholder="Hasta adı soyadı..."
                  className={`flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 ${a.inputRing} focus:border-transparent transition-all`}
                />
                {field.key === 'patient_name' && patientOptions.length > 0 && (
                  <datalist id="patient-names-list">
                    {patientOptions.map((name) => (
                      <option key={name} value={name} />
                    ))}
                  </datalist>
                )}
              </>
            )}
            {field.type === 'phone' && (
              <input
                type="tel"
                value={patientData[field.key] as string}
                onChange={(e) => onPatientChange(field.key, e.target.value)}
                placeholder="05XX XXX XX XX (Opsiyonel)"
                className={`flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 ${a.inputRing} focus:border-transparent transition-all`}
              />
            )}
            {field.type === 'clinic' && (
              <div className="flex-1 relative" ref={clinicRef}>
                <button
                  type="button"
                  onClick={() => { setClinicDropdownOpen((prev) => !prev); setClinicSearch(''); }}
                  className={`w-full flex items-center justify-between px-3 py-2 border rounded-lg text-sm transition-all cursor-pointer ${
                    patientData.clinic
                      ? `${a.triggerActive} font-medium`
                      : `border-gray-200 text-gray-400 bg-white ${a.triggerHover}`
                  }`}
                >
                  <span className="flex items-center gap-2 truncate">
                    {patientData.clinic ? (
                      <>
                        <i className={`ri-hospital-line ${a.hospitalIcon} text-sm flex-shrink-0`}></i>
                        <span className="truncate">{patientData.clinic}</span>
                      </>
                    ) : (
                      <span className="text-gray-400">Klinik seçin veya yazın...</span>
                    )}
                  </span>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    {patientData.clinic && (
                      <span
                        role="button"
                        onClick={(e) => { e.stopPropagation(); onPatientChange('clinic', ''); setClinicDropdownOpen(false); }}
                        className={`w-5 h-5 flex items-center justify-center rounded-full ${a.clearHover} cursor-pointer`}
                      >
                        <i className="ri-close-line text-xs"></i>
                      </span>
                    )}
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${clinicDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>

                {clinicDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl overflow-hidden z-30 shadow-lg">
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input
                          type="text"
                          value={clinicSearch}
                          onChange={(e) => setClinicSearch(e.target.value)}
                          placeholder="Klinik ara veya yeni yaz..."
                          autoFocus
                          className={`w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 ${a.inputRing} focus:border-transparent transition-all`}
                        />
                      </div>
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {clinicOptions
                        .filter((c) => c.toLowerCase().includes(clinicSearch.toLowerCase()))
                        .map((clinic) => (
                          <button
                            key={clinic}
                            type="button"
                            onClick={() => { onPatientChange('clinic', clinic); setClinicDropdownOpen(false); setClinicSearch(''); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm ${a.listItemHover} transition-colors cursor-pointer text-left ${
                              patientData.clinic === clinic ? `${a.listItemActive} font-semibold` : 'text-gray-700'
                            }`}
                          >
                            <div className={`w-7 h-7 rounded-lg ${a.listAvatarBg} font-bold text-xs flex items-center justify-center flex-shrink-0`}>
                              {clinic.slice(0, 1).toUpperCase()}
                            </div>
                            <span className="truncate">{clinic}</span>
                            {patientData.clinic === clinic && (
                              <i className={`ri-check-line ${a.listCheck} ml-auto flex-shrink-0`}></i>
                            )}
                          </button>
                        ))}

                      {clinicSearch.trim() &&
                        !clinicOptions.some((c) => c.toLowerCase() === clinicSearch.toLowerCase()) && (
                          <button
                            type="button"
                            onClick={() => { onPatientChange('clinic', clinicSearch.trim()); setClinicDropdownOpen(false); setClinicSearch(''); }}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm ${a.addHover} transition-colors cursor-pointer text-left ${a.addText} border-t border-gray-100`}
                          >
                            <div className={`w-7 h-7 rounded-lg ${a.addBg} text-white text-xs flex items-center justify-center flex-shrink-0`}>
                              <i className="ri-add-line"></i>
                            </div>
                            <span>&quot;{clinicSearch.trim()}&quot; olarak ekle</span>
                          </button>
                        )}

                      {clinicOptions.length === 0 && !clinicSearch.trim() && (
                        <div className="px-3 py-4 text-center text-xs text-gray-400">
                          <i className="ri-hospital-line text-2xl text-gray-300 block mb-1"></i>
                          Kayıtlı klinik yok — klinik adı yazabilirsiniz
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {field.type === 'options' && field.options && (
              <div className="flex gap-2">
                {field.options.map((opt) => {
                  const current = patientData[field.key as keyof PatientFormData];
                  const isActive = current === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => onPatientChange(field.key, opt.value)}
                      className={`
                        w-10 h-10 rounded-lg text-sm font-bold transition-all cursor-pointer
                        ${isActive
                          ? `${a.btnActive} scale-105`
                          : `bg-gray-100 text-gray-500 ${a.btnHover}`
                        }
                      `}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
